import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Star, Plus, Trash, Trophy, Sliders, X, FloppyDisk, Calculator, PencilSimple,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant étoiles 1-5 cliquables
function StarRating({ value, onChange, disabled, dataTestid }) {
  const v = value || 0;
  return (
    <div className="flex gap-0.5" data-testid={dataTestid}>
      {[1, 2, 3, 4, 5].map(n => {
        const active = v >= n - 0.5;
        const half = v >= n - 0.5 && v < n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => !disabled && onChange(n)}
            disabled={disabled}
            className="hover:scale-110 transition-transform disabled:opacity-50 disabled:hover:scale-100"
            title={`${n}/5`}
            data-testid={dataTestid ? `${dataTestid}-star-${n}` : undefined}
          >
            <Star
              size={18}
              weight={active ? 'fill' : 'regular'}
              className={half ? 'text-[#D97706]' : active ? 'text-[#D97706]' : 'text-[#D0D0D0]'}
            />
          </button>
        );
      })}
      {v > 0 && <span className="text-xs text-[#6B6B6B] ml-2 self-center">{v.toFixed(1)}</span>}
    </div>
  );
}

// Modal de configuration des critères
function NotationConfigModal({ chantierId, dceId, currentConfig, onSaved, onClose }) {
  const [criteres, setCriteres] = useState(currentConfig?.criteres || []);
  const [saving, setSaving] = useState(false);

  const totalPond = criteres.reduce((s, c) => s + Number(c.ponderation || 0), 0);
  const isValid = Math.abs(totalPond - 100) < 0.01 && criteres.length > 0;

  const addCritere = () => {
    setCriteres(prev => [...prev, {
      id: `crit_${Date.now()}`,
      label: '',
      ponderation: 0,
      type: 'manual',
    }]);
  };

  const removeCritere = (idx) => {
    setCriteres(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCritere = (idx, field, value) => {
    setCriteres(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const save = async () => {
    if (!isValid) {
      toast.error(`Les pondérations doivent sommer à 100 (actuellement ${totalPond.toFixed(1)})`);
      return;
    }
    // Vérif IDs uniques
    const ids = criteres.map(c => c.id?.trim()).filter(Boolean);
    if (new Set(ids).size !== ids.length) {
      toast.error('Les identifiants de critères doivent être uniques');
      return;
    }
    if (criteres.some(c => !c.label?.trim())) {
      toast.error('Tous les critères doivent avoir un libellé');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/notation/config`,
        {
          criteres: criteres.map(c => ({
            id: c.id?.trim(),
            label: c.label.trim(),
            ponderation: Number(c.ponderation),
            type: c.type,
          })),
        },
        { withCredentials: true }
      );
      toast.success('Critères enregistrés');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="notation-config-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }} className="flex items-center gap-2">
            <Sliders size={18} /> Configurer les critères de notation
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-[#6B6B6B]">
            Définissez les critères et leur pondération (somme = 100). Le critère <strong>Prix</strong> de type <em>auto</em>
            est calculé automatiquement (meilleure offre = 5/5). Les autres critères se notent manuellement de 1 à 5 étoiles.
          </p>

          {/* Header tableau */}
          <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wider text-[#6B6B6B] font-bold border-b border-[#E0E0DB] pb-2">
            <div className="col-span-3">Identifiant</div>
            <div className="col-span-5">Libellé</div>
            <div className="col-span-2 text-right">Pondération %</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-1"></div>
          </div>

          {criteres.map((c, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`config-critere-row-${idx}`}>
              <Input
                value={c.id}
                onChange={e => updateCritere(idx, 'id', e.target.value)}
                placeholder="prix, delai..."
                className="col-span-3 rounded-none text-xs font-mono"
                data-testid={`config-critere-id-${idx}`}
              />
              <Input
                value={c.label}
                onChange={e => updateCritere(idx, 'label', e.target.value)}
                placeholder="Libellé affiché"
                className="col-span-5 rounded-none text-xs"
                data-testid={`config-critere-label-${idx}`}
              />
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={c.ponderation}
                onChange={e => updateCritere(idx, 'ponderation', e.target.value)}
                className="col-span-2 rounded-none text-xs text-right"
                data-testid={`config-critere-pond-${idx}`}
              />
              <select
                value={c.type}
                onChange={e => updateCritere(idx, 'type', e.target.value)}
                className="col-span-1 px-1 py-1 border border-[#E0E0DB] text-[10px] rounded-none"
                data-testid={`config-critere-type-${idx}`}
              >
                <option value="manual">Manuel</option>
                <option value="auto">Auto</option>
              </select>
              <button
                onClick={() => removeCritere(idx)}
                className="col-span-1 text-[#8B3A3A] hover:bg-[#FFE4E4] p-1 justify-self-end"
                title="Supprimer ce critère"
                data-testid={`config-critere-delete-${idx}`}
              >
                <Trash size={14} />
              </button>
            </div>
          ))}

          <Button
            variant="outline"
            onClick={addCritere}
            className="rounded-none gap-2 w-full border-dashed"
            data-testid="config-add-critere"
          >
            <Plus size={14} /> Ajouter un critère
          </Button>

          {/* Total pondération */}
          <div className={`flex items-center justify-between p-3 border-2 ${isValid ? 'border-[#2D6A4F] bg-[#E8F5E9]' : 'border-[#C75B39] bg-[#FFE4E4]'}`}>
            <span className="text-sm font-bold uppercase tracking-wider">Total pondération</span>
            <span className={`text-lg font-bold ${isValid ? 'text-[#2D6A4F]' : 'text-[#C75B39]'}`} data-testid="config-total-pond">
              {totalPond.toFixed(1)} / 100
            </span>
          </div>

          {/* Suggestions rapides */}
          <div className="border-t border-[#E0E0DB] pt-3">
            <p className="text-xs text-[#6B6B6B] mb-2">Modèles types :</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCriteres([
                  { id: 'prix', label: 'Prix', ponderation: 50, type: 'auto' },
                  { id: 'delai', label: 'Délai', ponderation: 20, type: 'manual' },
                  { id: 'memoire_tech', label: 'Mémoire technique', ponderation: 20, type: 'manual' },
                  { id: 'references', label: 'Références', ponderation: 10, type: 'manual' },
                ])}
                className="rounded-none text-[11px]"
                data-testid="config-preset-classique"
              >
                Classique 50/20/20/10
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCriteres([
                  { id: 'prix', label: 'Prix', ponderation: 60, type: 'auto' },
                  { id: 'delai', label: 'Délai', ponderation: 15, type: 'manual' },
                  { id: 'memoire_tech', label: 'Mémoire technique', ponderation: 15, type: 'manual' },
                  { id: 'references', label: 'Références', ponderation: 10, type: 'manual' },
                ])}
                className="rounded-none text-[11px]"
                data-testid="config-preset-prix-fort"
              >
                Prix fort 60/15/15/10
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCriteres([
                  { id: 'prix', label: 'Prix', ponderation: 100, type: 'auto' },
                ])}
                className="rounded-none text-[11px]"
                data-testid="config-preset-prix-only"
              >
                Prix uniquement
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="config-cancel">Annuler</Button>
            <Button
              onClick={save}
              disabled={!isValid || saving}
              className="bg-[#1E3A5F] hover:bg-[#2C4F7F] text-white rounded-none gap-2"
              data-testid="config-save"
            >
              <FloppyDisk size={14} /> Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Composant principal : panneau notation par lot
export default function NotationDCE({ chantierId, dceId, onAttribuer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/notation`,
        { withCredentials: true }
      );
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur chargement notation');
    } finally { setLoading(false); }
  }, [chantierId, dceId]);

  useEffect(() => { reload(); }, [reload]);

  const updateNote = async (lotId, entrepriseId, critereId, note, override) => {
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/notation/${lotId}/${entrepriseId}/${critereId}`,
        { note, override },
        { withCredentials: true }
      );
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement note');
    }
  };

  if (loading || !data) {
    return <div className="p-6 text-sm text-[#6B6B6B]">Chargement de la notation…</div>;
  }

  const criteres = data.config?.criteres || [];
  const lotsArr = Object.values(data.lots || {});
  const noConfig = criteres.length === 0;

  return (
    <div className="space-y-5" data-testid="notation-dce-panel">
      {/* Header config */}
      <div className="flex items-center justify-between p-3 bg-[#FAFAF9] border border-[#E0E0DB]">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Notation multicritère
          </h3>
          <p className="text-xs text-[#6B6B6B] mt-1">
            {noConfig
              ? 'Configurez vos critères pour commencer'
              : `${criteres.length} critère${criteres.length > 1 ? 's' : ''} · pondérations cumulées ${Number(data.ponderation_totale ?? criteres.reduce((t, c) => t + Number(c.ponderation || 0), 0)).toLocaleString('fr-FR')} %`}
            {/* A6 (P2#4) — total exact ; alerte s'il ne fait pas 100 % */}
            {!noConfig && Math.abs(Number(data.ponderation_totale ?? 100) - 100) > 0.01 && (
              <span className="ml-2 text-[#C53030] font-semibold">à corriger (le total doit faire 100 %)</span>
            )}
          </p>
        </div>
        <Button
          onClick={() => setConfigOpen(true)}
          variant="outline"
          size="sm"
          className="rounded-none gap-2 border-[#1E3A5F] text-[#1E3A5F]"
          data-testid="open-notation-config"
        >
          <Sliders size={14} /> Configurer les critères
        </Button>
      </div>

      {noConfig && (
        <div className="p-6 text-center bg-[#FFF3E0] border border-[#F5D5B3]" data-testid="notation-no-config">
          <p className="text-sm text-[#6B4A24] mb-3">
            Aucun critère défini. Pour évaluer les offres au-delà du seul prix, configurez vos critères pondérés.
          </p>
          <Button
            onClick={() => setConfigOpen(true)}
            className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2"
            data-testid="notation-cta-config"
          >
            <Sliders size={14} /> Configurer maintenant
          </Button>
        </div>
      )}

      {/* Tableau notation par lot */}
      {!noConfig && lotsArr.map(lot => {
        const ents = lot.entreprises || [];
        if (ents.length === 0) return (
          <div key={lot.lot_id} className="border border-[#E0E0DB]" data-testid={`notation-lot-${lot.lot_id}`}>
            <div className="bg-[#F5F5F3] px-4 py-3">
              <span className="font-semibold text-[#2C2C2C]">{lot.lot_nom}</span>
              <span className="ml-3 text-xs text-[#9A9A9A]">Aucune offre soumise</span>
            </div>
          </div>
        );
        const winnerId = ents[0]?.entreprise_id;

        return (
          <div key={lot.lot_id} className="border border-[#E0E0DB]" data-testid={`notation-lot-${lot.lot_id}`}>
            <div className="bg-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between">
              <span className="font-semibold" style={{ fontFamily: 'DM Serif Display, serif' }}>
                {lot.numero ? `${/^\d+$/.test(String(lot.numero)) ? `Lot ${lot.numero}` : lot.numero} — ` : ''}{lot.lot_nom}
              </span>
              <span className="text-xs text-[#FAF9F6]">
                {ents.length} offre{ents.length > 1 ? 's' : ''} · meilleure offre {lot.min_montant_ht ? `${lot.min_montant_ht.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €` : '—'}
              </span>
            </div>

            {/* Une carte par entreprise classée */}
            <div className="divide-y divide-[#E0E0DB]">
              {ents.map((ent, idx) => {
                const isWinner = ent.entreprise_id === winnerId && ent.score_total > 0;
                return (
                  <div
                    key={ent.entreprise_id}
                    className={`p-4 ${isWinner ? 'bg-[#E8F5E9]' : ''}`}
                    data-testid={`notation-ent-${lot.lot_id}-${ent.entreprise_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center font-bold text-lg ${
                          ent.rang === 1 ? 'bg-[#FFD700] text-[#6B4A24]' :
                          ent.rang === 2 ? 'bg-[#C0C0C0] text-[#2C2C2C]' :
                          ent.rang === 3 ? 'bg-[#CD7F32] text-white' :
                          'bg-[#F5F5F3] text-[#6B6B6B]'
                        }`} data-testid={`notation-rang-${ent.entreprise_id}`}>
                          #{ent.rang}
                        </div>
                        <div>
                          <p className="font-semibold text-[#2C2C2C] flex items-center gap-2">
                            {ent.entreprise_nom}
                            {isWinner && <Trophy size={16} weight="fill" className="text-[#D97706]" />}
                          </p>
                          <p className="text-xs text-[#6B6B6B]">
                            Offre : <span className="font-medium text-[#2D6A4F]">{(ent.montant_lot_ht || 0).toLocaleString('fr-FR')} €</span>
                            {ent.is_lowest_price && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[#E8F5E9] text-[#2D6A4F] font-bold uppercase">Moins-disant</span>}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Score total</p>
                        <p className={`text-2xl font-bold ${isWinner ? 'text-[#2D6A4F]' : 'text-[#1E3A5F]'}`} data-testid={`notation-score-${ent.entreprise_id}`}>
                          {ent.score_total.toFixed(2)}<span className="text-sm font-normal text-[#9A9A9A]">/5</span>
                        </p>
                      </div>
                    </div>

                    {/* Notes par critère */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      {ent.par_critere.map(crit => {
                        const isAuto = crit.type === 'auto';
                        return (
                          <div key={crit.id} className="border border-[#E0E0DB] p-2 bg-white">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-medium text-[#2C2C2C]">
                                {crit.label}
                                <span className="ml-2 text-[10px] text-[#9A9A9A]">×{crit.ponderation}%</span>
                                {isAuto && !crit.override && (
                                  <span className="ml-2 text-[9px] uppercase font-bold text-[#1E3A5F] bg-[#E3F2FD] px-1.5 py-0.5">
                                    <Calculator size={9} className="inline mr-0.5" />Auto
                                  </span>
                                )}
                              </span>
                              <span className="text-[10px] text-[#6B6B6B]">
                                {crit.note !== null && crit.note !== undefined ? `+${crit.weighted.toFixed(2)}` : 'Non noté'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <StarRating
                                value={crit.note || 0}
                                onChange={(v) => updateNote(lot.lot_id, ent.entreprise_id, crit.id, v, isAuto ? true : false)}
                                disabled={false}
                                dataTestid={`star-${lot.lot_id}-${ent.entreprise_id}-${crit.id}`}
                              />
                              {isAuto && crit.override && (
                                <button
                                  type="button"
                                  onClick={() => updateNote(lot.lot_id, ent.entreprise_id, crit.id, crit.note, false)}
                                  className="text-[10px] text-[#1E3A5F] underline hover:no-underline"
                                  title="Restaurer la note auto"
                                  data-testid={`reset-auto-${lot.lot_id}-${ent.entreprise_id}-${crit.id}`}
                                >
                                  Restaurer auto
                                </button>
                              )}
                              {isAuto && !crit.override && crit.note !== null && (
                                <span className="text-[10px] text-[#9A9A9A] italic flex items-center gap-1">
                                  <PencilSimple size={9} /> cliquez pour figer
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {onAttribuer && !isWinner && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAttribuer(lot.lot_id, ent.entreprise_id)}
                        className="rounded-none gap-1 text-xs"
                        data-testid={`notation-attribuer-${lot.lot_id}-${ent.entreprise_id}`}
                      >
                        <Trophy size={12} /> Attribuer ce lot à {ent.entreprise_nom}
                      </Button>
                    )}
                    {isWinner && onAttribuer && (
                      <Button
                        size="sm"
                        onClick={() => onAttribuer(lot.lot_id, ent.entreprise_id)}
                        className="rounded-none gap-1 text-xs bg-[#2D6A4F] hover:bg-[#3D8A6F] text-white"
                        data-testid={`notation-attribuer-${lot.lot_id}-${ent.entreprise_id}`}
                      >
                        <Trophy size={12} weight="fill" /> Attribuer à {ent.entreprise_nom} (recommandé)
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {configOpen && (
        <NotationConfigModal
          chantierId={chantierId}
          dceId={dceId}
          currentConfig={data.config}
          onSaved={reload}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}
